import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Zap, CheckCircle2, RefreshCw, Bot, CreditCard, ArrowUpRight, Activity, CircleDollarSign } from 'lucide-react';
import api from '../../services/api';
import { Toast } from '../../Toast';

type GatewaySlug = 'podpay' | 'slimpay' | 'easypix';
type WithdrawalGatewaySlug = 'podpay' | 'slimpay';

interface HealthData {
    podpay: { configured: boolean; online: boolean; balance: { amount: number; waitingFunds: number } | null };
    slimpay: { configured: boolean; online: boolean };
    easypix: { configured: boolean; online: boolean };
}

interface GatewaySettings {
    active_gateway: GatewaySlug;
    active_withdrawal_gateway: WithdrawalGatewaySlug;
    podpay_configured_via_env: boolean;
    slimpay_configured_via_env: boolean;
    easypix_configured_via_env: boolean;
}

const LABELS: Record<GatewaySlug, string> = { podpay: 'PodPay', slimpay: 'Slimpay', easypix: 'Easy PIX' };

export const GatewaysList: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [settings, setSettings] = useState<GatewaySettings>({
        active_gateway: 'podpay',
        active_withdrawal_gateway: 'podpay',
        podpay_configured_via_env: false,
        slimpay_configured_via_env: false,
        easypix_configured_via_env: false
    });
    const [health, setHealth] = useState<HealthData | null>(null);
    const [loadingHealth, setLoadingHealth] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/gateway-settings');
            const d = res.data || {};
            setSettings({
                active_gateway: ['podpay', 'slimpay', 'easypix'].includes(d.active_gateway) ? d.active_gateway : 'podpay',
                active_withdrawal_gateway: d.active_withdrawal_gateway === 'slimpay' ? 'slimpay' : 'podpay',
                podpay_configured_via_env: !!d.podpay_configured_via_env,
                slimpay_configured_via_env: !!d.slimpay_configured_via_env,
                easypix_configured_via_env: !!d.easypix_configured_via_env
            });
        } catch {
        } finally {
            setLoading(false);
        }
    }, []);

    const loadHealth = useCallback(async () => {
        try {
            setLoadingHealth(true);
            const res = await api.get('/gateway-settings/health');
            setHealth(res.data);
        } catch {
        } finally {
            setLoadingHealth(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
        loadHealth();
    }, [loadSettings, loadHealth]);

    const saveField = async (field: string, value: GatewaySlug, label: string) => {
        try {
            setSaving(field);
            const payload: Record<string, string> = {};
            payload[field] = value;
            if (field === 'active_withdrawal_gateway') {
                payload.active_gateway = settings.active_gateway;
            }
            await api.post('/gateway-settings', payload);
            setSettings(prev => ({ ...prev, [field]: value }));
            setToast({ msg: `${label}: ${LABELS[value]} ativado.`, type: 'success' });
        } catch {
            setToast({ msg: 'Erro ao salvar.', type: 'error' });
        } finally {
            setSaving(null);
        }
    };

    const StatusDot: React.FC<{ online: boolean; configured: boolean }> = ({ online, configured }) => {
        if (!configured) return <span className="w-2.5 h-2.5 rounded-full bg-gray-600 inline-block" title="Não configurado" />;
        return <span className={`w-2.5 h-2.5 rounded-full inline-block ${online ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} title={online ? 'Online' : 'Offline'} />;
    };

    const GatewayCard: React.FC<{ slug: GatewaySlug }> = ({ slug }) => {
        const configMap: Record<GatewaySlug, boolean> = {
            podpay: health?.podpay.configured ?? settings.podpay_configured_via_env,
            slimpay: health?.slimpay.configured ?? settings.slimpay_configured_via_env,
            easypix: health?.easypix?.configured ?? settings.easypix_configured_via_env
        };
        const onlineMap: Record<GatewaySlug, boolean> = {
            podpay: health?.podpay.online ?? false,
            slimpay: health?.slimpay.online ?? false,
            easypix: health?.easypix?.online ?? false
        };
        const configured = configMap[slug];
        const online = onlineMap[slug];
        const balance = slug === 'podpay' ? health?.podpay.balance : null;
        const isUsedForPayments = settings.active_gateway === slug;
        const isUsedForWithdrawals = settings.active_withdrawal_gateway === slug;
        const usages: string[] = [];
        if (isUsedForPayments) usages.push('Cobranças');
        if (isUsedForWithdrawals) usages.push('Saques');

        return (
            <div className={`bg-white/[0.03] border rounded-2xl p-5 relative transition-all hover:bg-white/[0.05] ${configured && online ? 'border-white/10' : 'border-white/5'}`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${online ? 'bg-green-500/10 text-green-400' : configured ? 'bg-red-500/10 text-red-400' : 'bg-white/5 text-gray-600'}`}>
                            <Zap size={20} />
                        </div>
                        <div>
                            <h3 className="text-white font-bold text-sm flex items-center gap-2">
                                {LABELS[slug]}
                                <StatusDot online={online} configured={configured} />
                            </h3>
                            <p className="text-[10px] text-gray-500 font-mono">{slug}</p>
                        </div>
                    </div>
                    {usages.length > 0 && (
                        <div className="flex gap-1">
                            {usages.map(u => (
                                <span key={u} className="text-[9px] bg-orange-600/20 text-orange-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">{u}</span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-white/[0.03] rounded-xl p-3">
                        <p className="text-gray-500 uppercase text-[10px] mb-1">API Key</p>
                        <p className={`font-semibold ${configured ? 'text-green-400' : 'text-amber-400'}`}>
                            {configured ? 'Configurada' : 'Ausente'}
                        </p>
                    </div>
                    <div className="bg-white/[0.03] rounded-xl p-3">
                        <p className="text-gray-500 uppercase text-[10px] mb-1">Status</p>
                        <p className={`font-semibold ${!configured ? 'text-gray-500' : online ? 'text-green-400' : 'text-red-400'}`}>
                            {!configured ? 'N/A' : online ? 'Online' : 'Offline'}
                        </p>
                    </div>
                    {slug === 'podpay' && (
                        <>
                            <div className="bg-white/[0.03] rounded-xl p-3">
                                <p className="text-gray-500 uppercase text-[10px] mb-1">Saldo Disponível</p>
                                <p className="text-white font-bold">
                                    {balance ? `R$ ${balance.amount.toFixed(2).replace('.', ',')}` : '—'}
                                </p>
                            </div>
                            <div className="bg-white/[0.03] rounded-xl p-3">
                                <p className="text-gray-500 uppercase text-[10px] mb-1">A Receber</p>
                                <p className="text-gray-400 font-semibold">
                                    {balance ? `R$ ${balance.waitingFunds.toFixed(2).replace('.', ',')}` : '—'}
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const AssignmentRow: React.FC<{
        icon: React.ReactNode;
        title: string;
        desc: string;
        field: string;
        value: GatewaySlug;
        label: string;
        options?: GatewaySlug[];
    }> = ({ icon, title, desc, field, value, label, options }) => (
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-orange-600/10 text-orange-500 flex items-center justify-center shrink-0">
                    {icon}
                </div>
                <div className="min-w-0">
                    <h3 className="text-white font-bold text-sm">{title}</h3>
                    <p className="text-gray-500 text-xs truncate">{desc}</p>
                </div>
            </div>
            <div className="flex gap-2 shrink-0">
                {(options || (['podpay', 'slimpay', 'easypix'] as GatewaySlug[])).map(slug => {
                    const isSelected = value === slug;
                    const isSaving = saving === field;
                    return (
                        <button
                            key={slug}
                            onClick={() => !isSelected && saveField(field, slug, label)}
                            disabled={isSaving}
                            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                                isSelected
                                    ? 'bg-orange-600 text-white ring-1 ring-orange-500/50 shadow-lg shadow-orange-600/20'
                                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                            } ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                        >
                            {isSelected && <CheckCircle2 size={12} />}
                            {LABELS[slug]}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="p-6 max-w-5xl">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Shield className="text-orange-500" size={24} /> Adquirentes
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Gerencie os gateways de pagamento e saque</p>
                </div>
                <button
                    onClick={() => { loadSettings(); loadHealth(); }}
                    disabled={loadingHealth}
                    className={`p-2 rounded-xl bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all ${loadingHealth ? 'animate-spin' : ''}`}
                    title="Atualizar"
                >
                    <RefreshCw size={18} />
                </button>
            </div>

            {loading ? (
                <div className="p-20 text-center text-gray-500">Carregando...</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                        <GatewayCard slug="podpay" />
                        <GatewayCard slug="slimpay" />
                        <GatewayCard slug="easypix" />
                    </div>

                    <div className="mb-4">
                        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2 mb-1">
                            <Activity size={14} className="text-orange-500" /> Atribuição por função
                        </h2>
                        <p className="text-gray-600 text-xs">Defina qual gateway processa cada tipo de operação</p>
                    </div>

                    <div className="space-y-3">
                        <AssignmentRow
                            icon={<Bot size={20} />}
                            title="Pagamentos nos Bots"
                            desc="PIX gerado quando um membro compra acesso pelo bot do Telegram"
                            field="active_gateway"
                            value={settings.active_gateway}
                            label="Pagamentos nos Bots"
                        />
                        <AssignmentRow
                            icon={<CreditCard size={20} />}
                            title="Pagamentos dos Planos"
                            desc="PIX gerado na página de checkout dos planos (link público)"
                            field="active_gateway"
                            value={settings.active_gateway}
                            label="Pagamentos dos Planos"
                        />
                        <AssignmentRow
                            icon={<ArrowUpRight size={20} />}
                            title="Saques (PIX out)"
                            desc="Gateway que envia o PIX quando um saque é aprovado"
                            field="active_withdrawal_gateway"
                            value={settings.active_withdrawal_gateway}
                            label="Saques"
                            options={['podpay', 'slimpay']}
                        />
                    </div>

                    <div className="mt-6 bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                            <CircleDollarSign size={16} className="text-gray-600 mt-0.5 shrink-0" />
                            <div className="text-xs text-gray-500 space-y-1">
                                <p><strong className="text-gray-400">Bots + Planos</strong> compartilham o mesmo gateway (PodPay, Slimpay ou Easy PIX). Trocar um altera o outro automaticamente.</p>
                                <p><strong className="text-gray-400">Saques</strong> usam gateway independente (PodPay ou Slimpay) — Easy PIX não suporta PIX out.</p>
                                <p>Cada bot pode ter um <strong className="text-gray-400">override individual</strong> na página do bot em Gestão de Bots.</p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
